import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Check, AlertTriangle, Clock, ClipboardList } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

// Créneaux horaires de déclenchement (heure locale)
const TRIGGER_HOURS = [7, 12, 18];

// Tolérance : on déclenche entre H:00 et H:30 du créneau
const TRIGGER_WINDOW_MINUTES = 30;

function getCurrentTriggerSlot() {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  // Si on est dans la fenêtre [H:00, H:30] d'un créneau, on retourne ce créneau
  for (const hour of TRIGGER_HOURS) {
    if (h === hour && m < TRIGGER_WINDOW_MINUTES) {
      return `${now.toISOString().split('T')[0]}_${hour}h`;
    }
  }
  return null;
}

function isOverdue(deadline, status) {
  if (!deadline || status === 'done') return false;
  return new Date(deadline) < new Date(new Date().toDateString());
}

function formatDeadlineShort(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dtDay = new Date(dt);
    dtDay.setHours(0, 0, 0, 0);
    const diff = Math.round((dtDay - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "aujourd'hui";
    if (diff === 1) return 'demain';
    if (diff < 0) return `en retard de ${Math.abs(diff)}j`;
    if (diff <= 7) return `dans ${diff}j`;
    return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

/**
 * Modale de rappel des tâches en attente.
 * S'affiche automatiquement à 7h, 12h et 18h (une fois par créneau et par jour).
 * Permet de cocher directement les tâches comme faites.
 */
export default function TaskReminderModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentSlot, setCurrentSlot] = useState(null);

  const STORAGE_KEY = 'task_reminder_dismissed_slots';

  const getDismissedSlots = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch { return []; }
  };

  const markSlotDismissed = (slot) => {
    const slots = getDismissedSlots();
    if (!slots.includes(slot)) {
      slots.push(slot);
      // Nettoyage : garder uniquement les 30 derniers slots
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slots.slice(-30)));
    }
  };

  const loadAndOpenIfNeeded = useCallback(async (slot) => {
    if (!slot) return;
    const dismissed = getDismissedSlots();
    if (dismissed.includes(slot)) return; // Déjà vu/snoozé pour ce créneau
    setLoading(true);
    try {
      const res = await api.get('/api/pro-tasks?scope=mine&status_filter=pending');
      const data = res.data?.data || [];
      if (data.length === 0) return; // Pas de tâches → pas de popup
      setTasks(data);
      setCurrentSlot(slot);
      setOpen(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // Vérification au montage + toutes les 60 secondes
  useEffect(() => {
    const check = () => {
      const slot = getCurrentTriggerSlot();
      if (slot) loadAndOpenIfNeeded(slot);
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [loadAndOpenIfNeeded]);

  const handleDismiss = () => {
    if (currentSlot) markSlotDismissed(currentSlot);
    setOpen(false);
  };

  const handleViewTasks = () => {
    if (currentSlot) markSlotDismissed(currentSlot);
    setOpen(false);
    navigate('/golf/mes-taches');
  };

  const toggleDone = async (task) => {
    // Optimistic
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'done' } : t));
    try {
      await api.put(`/api/pro-tasks/${task.id}`, { status: 'done' });
      // Filtrer hors liste après 0.6s d'animation
      setTimeout(() => {
        setTasks(prev => {
          const next = prev.filter(t => t.id !== task.id);
          if (next.length === 0) {
            handleDismiss();
            toast.success('Bravo, toutes les tâches sont à jour !');
          } else {
            toast.success('Tâche marquée comme faite');
          }
          return next;
        });
      }, 600);
      window.dispatchEvent(new CustomEvent('refresh-badges'));
    } catch {
      setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'pending' } : t));
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Trier : retard en premier, puis haute priorité, puis le reste
  const sortedTasks = [...tasks].sort((a, b) => {
    const aOver = isOverdue(a.deadline, a.status) ? 1 : 0;
    const bOver = isOverdue(b.deadline, b.status) ? 1 : 0;
    if (aOver !== bOver) return bOver - aOver;
    const aHigh = a.priority === 'high' ? 1 : 0;
    const bHigh = b.priority === 'high' ? 1 : 0;
    if (aHigh !== bHigh) return bHigh - aHigh;
    return (a.deadline || '') < (b.deadline || '') ? -1 : 1;
  });

  const overdueCount = tasks.filter(t => isOverdue(t.deadline, t.status)).length;

  if (loading || !open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden border border-neutral-200" data-testid="task-reminder-modal">
        {/* Header minimaliste */}
        <div className="px-5 py-4 border-b border-neutral-100 bg-white">
          <div className="flex items-start gap-3">
            <div className="bg-neutral-100 p-2 rounded-lg flex-shrink-0">
              <ClipboardList size={18} className="text-neutral-700" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-neutral-900 leading-tight">Rappel : tâches à faire</h2>
              <p className="text-xs text-neutral-500 mt-0.5">
                {tasks.length} tâche{tasks.length > 1 ? 's' : ''} en attente
                {overdueCount > 0 && (
                  <span className="ml-1 inline-flex items-center gap-1 text-red-600">
                    • <AlertTriangle size={10} /> {overdueCount} en retard
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="max-h-[40vh] overflow-y-auto p-3 space-y-1.5 bg-neutral-50">
          {sortedTasks.map(task => {
            const overdue = isOverdue(task.deadline, task.status);
            return (
              <div
                key={task.id}
                className={`bg-white rounded-md p-2.5 flex items-start gap-2.5 border ${overdue ? 'border-red-200' : 'border-neutral-200'} ${task.status === 'done' ? 'opacity-50' : ''}`}
                data-testid={`reminder-task-${task.id}`}
              >
                <button
                  onClick={() => toggleDone(task)}
                  disabled={task.status === 'done'}
                  className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    task.status === 'done'
                      ? 'bg-neutral-800 border-neutral-800 text-white'
                      : 'border-neutral-300 hover:border-neutral-800'
                  }`}
                  title="Marquer comme fait"
                  data-testid={`reminder-toggle-${task.id}`}
                >
                  {task.status === 'done' && <Check size={12} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-sm ${task.status === 'done' ? 'line-through text-neutral-400' : 'text-neutral-800'}`}>
                      {task.title}
                    </span>
                    {task.priority === 'high' && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-red-700 font-medium">
                        <AlertTriangle size={9} /> Haute
                      </span>
                    )}
                  </div>
                  {task.deadline && (
                    <div className={`text-[11px] mt-0.5 inline-flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : 'text-neutral-500'}`}>
                      <Clock size={10} /> {formatDeadlineShort(task.deadline)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="p-3 border-t border-neutral-100 bg-white flex gap-2">
          <Button
            variant="outline"
            onClick={handleDismiss}
            className="flex-1 border-neutral-200 text-neutral-700 hover:bg-neutral-50"
            data-testid="reminder-dismiss-btn"
          >
            J'ai compris
          </Button>
          <Button
            onClick={handleViewTasks}
            className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white"
            data-testid="reminder-view-btn"
          >
            Voir les tâches
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
